import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Library, Holding, PaginatedResponse } from '../types';

interface LibraryWithRelations extends Library {
  parent?: { id: number; name: string; city: string; state: string } | null;
  branches?: { id: number; name: string; city: string; state: string; status: string }[];
}

export default function LibraryDetail() {
  const { id } = useParams<{ id: string }>();
  const [library, setLibrary] = useState<LibraryWithRelations | null>(null);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ data: LibraryWithRelations }>(`/libraries/${id}`)
      .then((res) => setLibrary(res.data))
      .catch((err) => setError(err.message));

    api.get<PaginatedResponse<Holding>>(`/libraries/${id}/holdings`)
      .then((res) => setHoldings(res.data))
      .catch(() => {});
  }, [id]);

  if (error) return <p className="error">Error: {error}</p>;
  if (!library) return <p>Loading...</p>;

  const latestStats = library.library_stats?.[0];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>{library.name}</h1>
          <p>{library.city}, {library.state}</p>
        </div>
        <Link to={`/libraries/${library.id}/edit`}><button>Edit</button></Link>
      </div>

      <div className="card">
        <h2>Info</h2>
        <dl>
          <dt>System</dt><dd>{library.system_name || '—'}</dd>
          <dt>Branch Level</dt><dd>{library.branch_level}</dd>
          {library.parent && (
            <>
              <dt>Parent System</dt>
              <dd><Link to={`/libraries/${library.parent.id}`}>{library.parent.name}</Link> — {library.parent.city}, {library.parent.state}</dd>
            </>
          )}
          <dt>ILS Vendor</dt><dd>{library.ils_vendor || '—'}</dd>
          <dt>Ingest Method</dt><dd>{library.ingest_method}</dd>
          <dt>Status</dt><dd>{library.status}</dd>
          <dt>Confidence</dt><dd>{library.confidence}</dd>
        </dl>
      </div>

      {library.branches && library.branches.length > 0 && (
        <div className="card">
          <h2>Branches ({library.branches.length})</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>State</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {library.branches.map((b) => (
                <tr key={b.id}>
                  <td>{b.name}</td>
                  <td>{b.city}</td>
                  <td>{b.state}</td>
                  <td>{b.status}</td>
                  <td><Link to={`/libraries/${b.id}`}>Detail</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {latestStats && (
        <div className="card">
          <h2>Statistics (as of {latestStats.snapshot_date})</h2>
          <dl>
            <dt>Uniqueness Score</dt>
            <dd>{latestStats.uniqueness_score?.toFixed(4) ?? '—'}</dd>
            <dt>Mean Holdcount</dt>
            <dd>{latestStats.mean_holdcount?.toFixed(1) ?? '—'}</dd>
            <dt>Median Holdcount</dt>
            <dd>{latestStats.median_holdcount?.toFixed(1) ?? '—'}</dd>
            <dt>Publisher Entropy</dt>
            <dd>{latestStats.publisher_entropy?.toFixed(3) ?? '—'}</dd>
            <dt>Subject Entropy</dt>
            <dd>{latestStats.subject_entropy?.toFixed(3) ?? '—'}</dd>
            <dt>Big 5 Concentration</dt>
            <dd>{latestStats.big5_concentration != null ? `${(latestStats.big5_concentration * 100).toFixed(1)}%` : '—'}</dd>
            <dt>Total Holdings</dt>
            <dd>{latestStats.total_holdings?.toLocaleString() ?? '—'}</dd>
          </dl>
        </div>
      )}

      <div className="card">
        <h2>Holdings Sample</h2>
        {holdings.length === 0 ? (
          <p>No holdings loaded yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ISBN</th>
                <th>Title</th>
                <th>Author</th>
                <th>Snapshot</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => (
                <tr key={h.id}>
                  <td>{h.isbn}</td>
                  <td>{h.book?.title ?? '—'}</td>
                  <td>{h.book?.author ?? '—'}</td>
                  <td>{h.snapshot_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
